import { SaveOutlined, PrinterOutlined, MoneyCollectOutlined } from '@ant-design/icons';
import { Button, Checkbox, Col, Form, Input, InputNumber, message, Modal, Row, Spin, Tooltip } from 'antd';
import React, { useState } from 'react';
import ZTable, { IFilterItem, ITableRef, IColumnType } from '@/components/ZTable';
import { useTranslation } from 'react-i18next';
import { ModalWidth, VALIDATE_REQUIRED } from '@/config';
import CountryInput from '@/components/FormItems/CountryInput';
import { BlobStorageContainerType, TransportRoleType } from '@/constants';
import CreateBtn from '@/components/app/CreateBtn';
import MainContent from '@/layouts/MainContent';
import { Helmet } from 'react-helmet';
import RegionInput from '@/components/FormItems/RegionInput';
import CityInput from '@/components/FormItems/CityInput';
import CompanyUserInput from '@/components/FormItems/CompanyUserInput';
import SingleImageUpload from '@/components/FormItems/SingleImageUpload';
import PrinterConfigurationsModal from './PrinterConfigurationsModal';
import ManageBalanceModal from './ManageBalanceModal';
import { clearAllStationsCache } from '@/hooks/data/masterDataHook';
import { StationModel } from '@/__generated';
import { apiSdk } from '@/httpClient';
import BooleanTag from '@/components/app/BooleanTag';
import useHasPermissions from '@/hooks/useHasPermissions';
import { APP_PERMISSIONS } from '@/services/roleService';
const PAGE_TITLE = 'stationsPage_Title';

const columns: IColumnType<StationModel>[] = [
  {
    title: 'stationsPage_StationName',
    dataIndex: 'name',
    width: 150,
    ellipsis: true,
  },
  {
    title: 'stationsPage_StationCode',
    dataIndex: 'stationCode',
    width: 100,
    ellipsis: true,
  },
  {
    title: 'stationsPage_IsGlobalStation',
    dataIndex: 'companyId',
    width: 120,
    align: 'center',
    render: v => <BooleanTag value={!v} />,
  },
  {
    title: 'base.PhoneNumber',
    dataIndex: 'phoneNumber',
    width: 100,
    ellipsis: true,
  },
  {
    title: 'base.Email',
    dataIndex: 'email',
    width: 150,
    ellipsis: true,
  },
  {
    title: 'stationsPage_CurrentBalance',
    dataIndex: 'currentBalance',
    ellipsis: true,
    width: 120,
  },
  {
    title: 'stationsPage_QuartierName',
    dataIndex: 'quartierName',
    ellipsis: true,
    width: 100,
  },
  {
    title: 'base.Address',
    dataIndex: 'address',
    ellipsis: true,
    width: 100,
  },
  {
    title: 'stationsPage_CityRegionCountry',
    dataIndex: 'id',
    render: (v, record) => `${record.cityName}-${record.regionName}-${record.countryName}`,
    ellipsis: true,
    width: 200,
  },
];

const filters: Array<IFilterItem> = [
  { type: 'TEXT', name: 'name', label: 'stationsPage_StationName' },
  { type: 'TEXT', name: 'stationCode', label: 'stationsPage_StationCode' },
];

const AccountTab = () => {
  const { t } = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [form] = Form.useForm();
  const [formVisible, setFormVisible] = useState(false);
  const [isNewForm, setIsNewForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stationPrinterId, setStationPrinterId] = React.useState<string>();
  const [stationBalanceId, setStationBalanceId] = React.useState<string>();
  const canUpdateStation = useHasPermissions([APP_PERMISSIONS.ManageStations]);
  const canUpdateStationBalance = useHasPermissions([APP_PERMISSIONS.ManageStationBalance]);
  const canUpdateStationPrinter = useHasPermissions([APP_PERMISSIONS.ManageStationPrinter]);

  const clearCache = () => {
    clearAllStationsCache();
  };

  const createEntity = values => {
    setLoading(true);
    apiSdk.StationService.create({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
          clearCache();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const updateEntity = values => {
    setLoading(true);
    apiSdk.StationService.update({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
          clearCache();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(() => {
        message.error(t('InvalidLoginAttempt'));
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const onEdit = (id: string) => {
    setLoading(true);
    setFormVisible(true);
    apiSdk.StationService.getDetail({ id: id })
      .then(res => {
        if (res) {
          setIsNewForm(false);

          form.setFieldsValue({
            ...res,
            stationAdministratorUserIds: res.stationAdministratorUsers?.map(x => x.userId),
            stationSupervisorUserIds: res.stationSupervisorUsers?.map(x => x.userId),
          });
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const countryId = Form.useWatch('countryId', form);
  const regionId = Form.useWatch('regionId', form);
  const companyId = Form.useWatch('companyId', form);
  const isGlobalStation = isNewForm ? false : !companyId;
  return (
    <MainContent title={t(PAGE_TITLE)}>
      <Helmet title={t(PAGE_TITLE)} />
      <div className="ant-layout-content-body">
        <ZTable<StationModel>
          columns={columns}
          primaryKey="id"
          tableRef={zTb}
          url="/fleets/stations/get-stations"
          bordered
          order_by="createdOn"
          order_by_asc={true}
          allowEdit={canUpdateStation}
          onEdit={record => {
            onEdit(record.id || '');
          }}
          allowDelete={canUpdateStation}
          showDelete={v => !!v.companyId}
          delete_url="/fleets/stations/delete"
          filters={filters}
          searchBtnAlignLeft
          actionColumnWidth={150}
          extraActions={[
            {
              render: record => {
                return canUpdateStationBalance ? (
                  <Tooltip title={t('stationsPage_ManageBalance')}>
                    <Button size="small" onClick={() => setStationBalanceId(record.id)} type="primary" icon={<MoneyCollectOutlined />} />
                  </Tooltip>
                ) : null;
              },
            },
            {
              render: record => {
                return canUpdateStationPrinter ? (
                  <Tooltip title={t('stationsPage_PrinterConfigurations')}>
                    <Button size="small" onClick={() => setStationPrinterId(record.id)} type="ghost" icon={<PrinterOutlined />} />
                  </Tooltip>
                ) : null;
              },
            },
          ]}
          extraBtns={
            canUpdateStation ? (
              <CreateBtn
                onClick={() => {
                  setIsNewForm(true);
                  setFormVisible(true);
                  form.resetFields();
                }}
              />
            ) : null
          }
          onDeleteCompleted={clearCache}
        ></ZTable>
        {formVisible ? (
          <Modal
            open={formVisible}
            maskClosable={false}
            width={ModalWidth.large}
            className="nta-custom-form"
            title={isNewForm ? t('stationsPage_AddStation') : t('stationsPage_UpdateStation')}
            forceRender={true}
            okText={t('save')}
            cancelText={t('cancel')}
            okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
            onCancel={() => {
              form.resetFields();
              setFormVisible(false);
            }}
            onOk={() => {
              form
                .validateFields()
                .then(values => {
                  if (values.id) {
                    updateEntity(values);
                  } else {
                    createEntity(values);
                  }
                })
                .catch(info => {});
            }}
          >
            <Spin spinning={loading}>
              <Form form={form} size="middle" layout="horizontal" labelAlign="right" labelCol={{ md: 10 }}>
                <Form.Item name="id" className="hidden">
                  <Input type="hidden" />
                </Form.Item>
                <Form.Item name="companyId" className="hidden">
                  <Input type="hidden" />
                </Form.Item>
                <Row gutter={6}>
                  <Col md={12}>
                    <Form.Item label={t('stationsPage_StationName')} name="name" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <Input disabled={isGlobalStation} className="input-value-color" />
                    </Form.Item>

                    <Form.Item label={t('stationsPage_StationCode')} name="stationCode" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <Input disabled={isGlobalStation} className="input-value-color" />
                    </Form.Item>

                    <Form.Item label={t('stationsPage_StationAdministrator')} name="stationAdministratorUserIds">
                      <CompanyUserInput mode="multiple" role={TransportRoleType.StationAdministrator} />
                    </Form.Item>

                    <Form.Item label={t('stationsPage_StationSupervisor')} name="stationSupervisorUserIds">
                      <CompanyUserInput mode="multiple" role={TransportRoleType.StationSupervisor} />
                    </Form.Item>
                    <Form.Item
                      label={t('stationsPage_ExpenseArpprover')}
                      name="expenseApproverId"
                      rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                    >
                      <CompanyUserInput role={TransportRoleType.Treasurer} />
                    </Form.Item>
                    <Form.Item
                      label={t('stationsPage_StartingBalance')}
                      name="startingBalance"
                      rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                    >
                      <InputNumber min={0} className="w-100" />
                    </Form.Item>

                    <Form.Item label={t('base.Country')} name="countryId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <CountryInput
                        disabled={isGlobalStation}
                        className="input-value-color"
                        onChange={() => {
                          form.setFieldValue('regionId', null);
                        }}
                      />
                    </Form.Item>
                    <Form.Item label={t('base.Region')} name="regionId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <RegionInput
                        className="input-value-color"
                        onChange={() => {
                          form.setFieldValue('cityId', null);
                        }}
                        countryId={countryId}
                        disabled={!countryId || isGlobalStation}
                      />
                    </Form.Item>
                    <Form.Item label={t('base.City')} name="cityId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <CityInput regionId={regionId} disabled={!regionId || isGlobalStation} className="input-value-color" />
                    </Form.Item>
                    <Form.Item label={t('stationsPage_Latitude')} name="latitude" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <InputNumber disabled={isGlobalStation} className="input-value-color w-100" />
                    </Form.Item>
                    <Form.Item label={t('stationsPage_Longitude')} name="longitude" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <InputNumber disabled={isGlobalStation} className="input-value-color w-100" />
                    </Form.Item>
                  </Col>
                  <Col md={12}>
                    <Form.Item label={t('stationsPage_Quartier')} name="quartierName">
                      <Input disabled={isGlobalStation} className="input-value-color w-100" />
                    </Form.Item>
                    <Form.Item label={t('base.PhoneNumber')} name="phoneNumber" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <Input disabled={isGlobalStation} className="input-value-color w-100" />
                    </Form.Item>

                    <Form.Item label={t('base.Email')} name="email">
                      <Input type="email" disabled={isGlobalStation} className="input-value-color w-100" />
                    </Form.Item>

                    <Form.Item label={t('stationsPage_displayPhoneNumbers')} name="displayPhoneNumber">
                      <Input disabled={isGlobalStation} className="input-value-color w-100" />
                    </Form.Item>
                    <Form.Item label={t('base.Address')} name="address">
                      <Input.TextArea disabled={isGlobalStation} className="input-value-color w-100" />
                    </Form.Item>
                    <Form.Item label="  " colon={false} name="isConnectionStation" valuePropName="checked">
                      <Checkbox disabled={isGlobalStation} className="input-value-color w-100">
                        {t('stationsPage_IsConnectionStation')}
                      </Checkbox>
                    </Form.Item>
                    <Form.Item name="stationImageUrl" label={t('stationsPage_StationImageUrl')}>
                      <SingleImageUpload blobStorageContainer={BlobStorageContainerType.StationsContainer} disabled={isGlobalStation} />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </Spin>
          </Modal>
        ) : null}
      </div>
      {stationPrinterId ? (
        <PrinterConfigurationsModal
          stationId={stationPrinterId}
          onClose={() => {
            setStationPrinterId(undefined);
          }}
        />
      ) : null}
      {stationBalanceId ? (
        <ManageBalanceModal
          stationId={stationBalanceId}
          onClose={(reload?: boolean) => {
            setStationBalanceId(undefined);
            if (reload) {
              zTb.current?.reload();
            }
          }}
        />
      ) : null}
    </MainContent>
  );
};
export default AccountTab;
